<template>
  <div class="average-rating large-margin-bottom-z medium-margin-bottom-xs small-margin-bottom-xs">
    <h3 class="h3 large-text-center large-margin-bottom-xxs">
      {{ averageRating }}&sol;5
    </h3>
    <div
      class="reviewlet large-margin-bottom-xxs"
      :data-testid="averageRatingReviewlet"
    >
      <Reviewlet
        class="normalizing"
        :rating="averageRating"
        :show-rating="false"
        :show-reviews-count="false"
        :show-review-text="false"
        size="large"
      />
    </div>
    <p
      class="reviews-count large large-text-center large-order-3 medium-order-4 small-order-4"
      :data-testid="averageRatingReviewsCount"
    >
      <template v-if="reviewsCount === totalCount">
        {{ totalCountFormatted }} star ratings & reviews
      </template>
      <template v-else>
        {{ totalCountFormatted }} star ratings, {{ reviewsCountFormatted }} reviews
      </template>
    </p>
    <a
      v-if="!isInternationalMode"
      class="secondary-cta large-order-4 medium-order-3 small-order-3 large-margin-top-m"
      :data-testid="averageRatingCta"
      :href="writeReviewUrl"
      @click="writeReviewFireBV"
    >
      {{ writeReviewCtaText }}
    </a>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import Reviewlet from '@atomic-ui/reviewlet';

import { useBrand } from '@composables/useBrand';
import { useProduct } from '@modules/product';
import { useFeatureEligibility, useProductFlags } from '@modules/featureControl';
import useSelectedMemberId from '@modules/latchkey/composables/useSelectedMemberId';
import { thousandsSeparator } from '@utils/thousandsSeparator';
import { trackEvent } from '@utils/bvPixel';
import { buildUrl } from '@utils/commonUtils';
import { averageRatingCta, averageRatingReviewlet, averageRatingReviewsCount } from '../../../constants/testIds';

const props = defineProps({
  totalCount: {
    type: Number,
    required: true,
  },
  reviewsCount: {
    type: Number,
    required: true,
  },
  averageRating: {
    type: Number,
    required: true,
  },
});

const { isMcom } = useBrand();
const { isInternationalMode } = useFeatureEligibility();
const writeReviewCtaText = `Write a ${isMcom ? 'R' : 'r'}eview`;

/** @type import('vue').Ref<string | number | null> */
const totalCountFormatted = computed(() => (props.totalCount ? thousandsSeparator(props.totalCount) : 0));
const reviewsCountFormatted = computed(() => (props.reviewsCount ? thousandsSeparator(props.reviewsCount) : 0));

const { productId } = useProduct();

const { isConsolidatedThirdVariantFlag } = useProductFlags();
const selectedMemberId = useSelectedMemberId(productId);

const writeReviewUrl = computed(() => buildUrl('/shop/product/review/add', {
  ID: productId,
  ...(isConsolidatedThirdVariantFlag
      && selectedMemberId.value && { memberId: selectedMemberId.value }),
}));

// BV track for stats - these run user clicks on Write a Review CTA
function writeReviewFireBV() {
  trackEvent('write', productId, 'review');
}
</script>

<style scoped lang="scss">
@import "~settingSCSS";

.average-rating {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;

  @include breakpoint(large) {
    flex-direction: column;
  }

  h3 {
    @include margin-right-xxs;

    @include breakpoint(large) {
      @include margin-right-z;
    }
  }
}

.reviewlet {
  flex-grow: 1;
}
.reviews-count {
  flex-basis: 100%;
  @include margin-top-xs;

  @include breakpoint(large) {
    @include margin-top-z;
  }
}
</style>
